import { AuthServiceInterface } from '@freelancer/auth/interface';
import {
  awardProjectRichMessage,
  CountryCode,
  fixedUsdProject,
  freelancerUser,
  graphicDesignSkill,
  hourlyUsdProject,
  logoDesignSkill,
  offsitingMessageReminderFreelancerRichMessage,
  pythonSkill,
  Skill,
  updateProjectViewProjectBidStats,
  User,
  userSkillsFromSkills,
  websiteDesignSkill,
} from '@freelancer/datastore/collections';
import { DatastoreInterface } from '@freelancer/datastore/core';
import {
  generateId,
  paragraphs,
  peopleWithSurnames,
} from '@freelancer/datastore/testing';
import { LoginTestingUsernames } from '@freelancer/login-signup/testing';
import { ContextTypeApi } from 'api-typings/messages/messages_types';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import {
  createBanners,
  createBids,
  createContestBudgetRanges,
  createCountries,
  createCurrencies,
  createDomain,
  createFreelancerReputations,
  createLanguage,
  createMessage,
  createMessages,
  createNotificationsPreferences,
  createOnlineOffline,
  createProjectBudgetOptions,
  createProjectUpgradeFees,
  createProjectViewBids,
  createProjectViewProject,
  createProjectViewUser,
  createProjectViewUsers,
  createReferralInvitationCheck,
  createSearchActiveProjects,
  createSiteStat,
  createSkills,
  createThread,
  createUser,
  createUserBalances,
  createUserCalifornianStatuses,
  createUserGiveGetDetail,
  createUserInfo,
  createUserRecentProjectsAndContests,
  createUsers,
  createUserSkills,
  createUsersProfile,
  createUsersSelf,
} from '../document-creators';
import {
  constructAppStoreProfileState,
  constructAppStoreProjectViewPagePaymentTabState,
  constructFreelancerProfilePageState,
  createAppStoreProfileUser,
} from './app-store';

/**
 * Create initial state
 *
 * Multiple personas are included here which you can enable by uncommenting them.
 */
export async function constructInitialStoreState(
  auth: AuthServiceInterface,
  datastore: DatastoreInterface,
  authUid: number,
) {
  constructGlobalState(authUid);

  switch (authUid) {
    case LoginTestingUsernames.basicEmployer:
      constructCommonUserState({ authUid });
      constructInitialEmployerStoreState(auth, datastore, authUid);
      break;
    case LoginTestingUsernames.basicFreelancer:
    case LoginTestingUsernames.experiencedEmployer:
    case LoginTestingUsernames.experiencedFreelancer:
      console.warn('These are presently blank, please help implementing them.');
      break;
    case LoginTestingUsernames.appStoreScreenshotsUser: {
      const employer = await createAppStoreProfileUser(authUid);
      constructInitialStoreStateForAppStoreScreenshots(employer);
      break;
    }
    default:
      break;
  }
}

// States not specific to a particular user,
// eg. supported currencies, skills.
export async function constructGlobalState(authUid: number) {
  await createSiteStat({ userId: authUid });
  await createLanguage();
  await createDomain();
  await createCurrencies();
  await createSkills();
  await createCountries({
    countryCodes: [CountryCode.US],
  });
}

// User related state that often required by most of the pages.
export async function constructCommonUserState({
  authUid,
  searchLanguages = ['en'],
  skills = [websiteDesignSkill(), graphicDesignSkill(), logoDesignSkill()],
}: {
  readonly authUid: number;
  readonly searchLanguages?: readonly string[];
  readonly skills?: readonly Skill[];
}) {
  await createUserSkills({
    userId: authUid,
    ...userSkillsFromSkills(skills),
  });
  await createUsersProfile({ userId: authUid, searchLanguages });
  await constructSearchProjectsState(authUid);
}

export async function constructInitialEmployerStoreState(
  auth: AuthServiceInterface,
  datastore: DatastoreInterface,
  authUid: number,
) {
  // Create self and other me things
  const { id: userId, username } = await createUsersSelf({
    userId: authUid,
  });
  const user = await createUser({ userId });

  // Used on the webapp playground
  await createUser({ userId: 13223 });
  await createUser({ userId: 133223 });

  await createUserInfo({ userId });
  await createUserGiveGetDetail({ userId, username });
  await createUserCalifornianStatuses({ userIds: [userId] });
  await createUserBalances();
  await createBanners();
  await createReferralInvitationCheck({ userId });
  await createNotificationsPreferences({});

  // Create project for PVP
  const project = await createProjectViewProject({
    projectId: 42,
    ownerId: userId,
  });
  const { id: projectId } = project;
  await createProjectUpgradeFees({ projectId });
  const bidders = await createUsers({ names: peopleWithSurnames });
  await createProjectViewUsers({
    users: [user, ...bidders],
  });

  const bidderIds = bidders.map(bidder => bidder.id);
  const bids = await createBids({
    bidderIds,
    projectId,
    projectOwnerId: userId,
    minAmount: 100,
    maxAmount: 200,
  });
  await createProjectViewBids({ bids });
  await updateProjectViewProjectBidStats(datastore, { projectId, bids });
  await createFreelancerReputations({ freelancerIds: bidderIds });
  await createOnlineOffline({ userIds: bidderIds });

  // Put project in nav
  await createUserRecentProjectsAndContests({ projects: [project] });

  // Create for PJP
  await createProjectBudgetOptions({
    projectType: ProjectTypeApi.FIXED,
  });
  await createProjectBudgetOptions({
    projectType: ProjectTypeApi.HOURLY,
  });
  await createContestBudgetRanges();

  // Create some messages
  const freelancerBotUser = await createUser({
    username: 'plagiarismDetector3000',
    displayName: 'Plagiarism Detector 3000',
  });

  const awardedThread = await createThread({
    userId: authUid,
    otherMembers: bidderIds.slice(0, 1),
    context: { type: ContextTypeApi.PROJECT, id: project.id },
  });
  await createMessage({
    fromUserId: authUid,
    threadId: awardedThread.id,
    message: 'Hi there',
  });

  await createMessage({
    ...awardProjectRichMessage({
      freelancerBotUser,
      thread: awardedThread,
      bidId: bids[0].id,
    }),
  });

  const offsitingThread = await createThread({
    userId: authUid,
    otherMembers: bidderIds.slice(1, 2),
    context: { type: ContextTypeApi.PROJECT, id: project.id },
  });
  await createMessage({
    fromUserId: authUid,
    threadId: offsitingThread.id,
    message: 'Pay me over Skype',
  });

  await createMessage({
    ...offsitingMessageReminderFreelancerRichMessage({
      freelancerBotUser,
      thread: offsitingThread,
    }),
  });

  /** A few threads reading books to you. */
  Promise.all(
    Object.values(paragraphs).map(async (paragraph, index) =>
      createMessages({
        fromUserId: authUid,
        threadId: generateId(),
        messages: paragraph,
      }),
    ),
  );
}

export async function constructSearchProjectsState(authUid: number) {
  await createLanguage();
  await createSearchActiveProjects({
    ...fixedUsdProject(),
    budget: {
      minimum: 150,
      maximum: 250,
    },
    bidStats: { bidCount: 2, bidAvg: 170 },
    skills: [websiteDesignSkill(), pythonSkill()],
    ownerId: generateId(),
    description: `I have a shop website for my special customer. I need to update website mobile friendly and input some functionalities. You must be versed in front-end and back-end coding.`,
    title: 'Website for my coffee shop',
  });
  await createSearchActiveProjects({
    ...fixedUsdProject(),
    budget: {
      minimum: 100,
    },
    bidStats: { bidCount: 2, bidAvg: 125 },
    skills: [logoDesignSkill()],
    ownerId: generateId(),
    description:
      'I am registering a company for my real estate business and I need a creative logo design.',
    title: 'New company logo design',
  });
  // We would like this project to show first in the search result.
  // Hence, we are creating it at last.
  await createSearchActiveProjects({
    ...hourlyUsdProject(),
    budget: {
      minimum: 23,
      maximum: 33,
    },
    bidStats: { bidCount: 2, bidAvg: 28 },
    skills: [graphicDesignSkill()],
    ownerId: generateId(),
    description: 'I need a 3D design with details drawings of my dream house.',
    title: 'Interior design illustrator',
  });
}

export async function constructInitialStoreStateForAppStoreScreenshots(
  employer: User,
) {
  // Create the App Store screenshot user profile.
  await constructAppStoreProfileState(employer);

  // Create a freelancer user for pages below.
  const freelancer = await createUser({
    ...freelancerUser(),
    // Fixed user ID so that we can have a constant avatar for app store screenshots.
    userId: 2,
    username: 'alexanderL',
    displayName: 'Alexander L.',
  });
  await createProjectViewUser({ user: freelancer });

  // Setup initial state for user profile page.
  await constructFreelancerProfilePageState({
    employer,
    freelancer,
  });

  // Setup common user state including the project search page
  // which is needed for screenshots.
  await constructCommonUserState({ authUid: employer.id });

  // Setup initial state for PVP payments tab.
  await constructAppStoreProjectViewPagePaymentTabState(
    employer.id,
    freelancer,
  );
}
